import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Shield } from "lucide-react";
import { useAuth } from "./hooks/useAuth";
import { Spinner } from "./components/shared/Spinner";
import { AppLayout } from "./components/AppLayout";
import { LoginScreen } from "./components/LoginScreen";

// Owner pages
import { OwnerOverview } from "./pages/owner/Overview";
import { OwnerBilling } from "./pages/owner/Billing";
import { OwnerBrainPage } from "./pages/owner/Brain";
import { OwnerTeam } from "./pages/owner/api/Team";
import { OwnerUsage } from "./pages/owner/api/Usage";

// Admin pages
import { AdminOverview } from "./pages/admin/Overview";
import { AdminOrganizations } from "./pages/admin/Organizations";
import { AdminUsersPage } from "./pages/admin/Users";
import { AdminProposals } from "./pages/admin/payments/Proposals";
import { AdminInvoices } from "./pages/admin/payments/Invoices";
import { AdminSubscriptions } from "./pages/admin/api/Subscriptions";
import { AdminPlans } from "./pages/admin/api/Plans";
import { AdminModelPricingPage } from "./pages/admin/api/ModelPricing";
import { AdminObservabilityPage } from "./pages/admin/Observability";
import { AdminAudit } from "./pages/admin/Audit";

// Member pages
import { MemberOverview } from "./pages/member/Overview";

// Account settings (open to every signed-in user)
import { SettingsPage } from "./pages/Settings";

// Auth pages (invite acceptance)
import { AcceptInvite } from "./pages/auth/AcceptInvite";
import { EmailLinkComplete } from "./pages/auth/EmailLinkComplete";

// Onboarding (Typeform-style first-run for enterprise owners)
import { OnboardingFlow } from "./components/onboarding/OnboardingFlow";
import { useOrgSnapshot } from "./hooks/useOrgSnapshot";

// Guided in-app tour. Provider lives inside the signed-in tree so the
// useNavigate dependency is available and unauthenticated routes
// (/accept, /legal/...) stay overlay-free.
import { TourProvider } from "./components/tour/TourProvider";

// Public legal pages
import { SubscriptionTerms } from "./pages/legal/SubscriptionTerms";
import { PrivacyPolicy } from "./pages/legal/PrivacyPolicy";

export default function App() {
  const { user, effectiveRole, loading, signOut } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes — reachable without an account. */}
        <Route path="/accept" element={<AcceptInvite />} />
        <Route path="/auth/email-link-complete" element={<EmailLinkComplete />} />
        <Route path="/legal/subscription-terms" element={<SubscriptionTerms />} />
        <Route path="/legal/privacy" element={<PrivacyPolicy />} />
        <Route
          path="/*"
          element={
            !user ? (
              <LoginScreen />
            ) : !effectiveRole ? (
              <AccessDenied
                email={user.email ?? ""}
                onSignOut={signOut}
              />
            ) : (
              <TourProvider>
                <Inside />
              </TourProvider>
            )
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

function Inside() {
  const { effectiveRole } = useAuth();
  if (effectiveRole === "enterprise_owner") {
    return <OwnerInside />;
  }
  if (effectiveRole === "enterprise_member") {
    return <MemberInside />;
  }
  return <RoleRoutes effectiveRole={effectiveRole} />;
}

// Owner-only wrapper: blocks the dashboard with the Typeform-style
// onboarding until `orgs/{orgId}.onboardingCompleted === true`. Migration
// note: orgs without the field (created before this rollout) read as
// `!== true` so they go through the onboarding once with pre-filled data.
function OwnerInside() {
  const { effectiveRole } = useAuth();
  const { org, loading } = useOrgSnapshot();
  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <Spinner />
      </div>
    );
  }
  const completed =
    (org as { onboardingCompleted?: boolean } | null)?.onboardingCompleted ===
    true;
  if (org && !completed) {
    return <OnboardingFlow mode="owner" />;
  }
  return <RoleRoutes effectiveRole={effectiveRole} />;
}

// Member-only wrapper: gates the dashboard on the per-user
// `users/{uid}.onboardingCompletedAt`. Owners can't be gated this way
// because the owner flow needs the org doc (fiscal, plans). Pre-existing
// members (provisioned before this field existed) read as `null` and pass
// through a one-time "Hola, ¿cómo te llamas?" + tour.
function MemberInside() {
  const { effectiveRole, profile } = useAuth();
  // `profile === null` means the user doc snapshot hasn't arrived yet. Wait
  // it out — rendering the onboarding while we still don't know the
  // completion timestamp would flash the form before snapping it away.
  if (!profile) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (!profile.onboarding_completed_at) {
    return <OnboardingFlow mode="member" />;
  }
  return <RoleRoutes effectiveRole={effectiveRole} />;
}

function RoleRoutes({ effectiveRole }: { effectiveRole: ReturnType<typeof useAuth>["effectiveRole"] }) {
  return (
    <Routes>
      <Route element={<AppLayout />}>
        {effectiveRole === "admin" && AdminRoutes()}
        {(effectiveRole === "enterprise_owner" ||
          effectiveRole === "institution") &&
          OwnerRoutes()}
        {effectiveRole === "enterprise_member" && MemberRoutes()}
        {/* Account settings page is available to every signed-in user. */}
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function AdminRoutes() {
  return (
    <>
      <Route path="/" element={<AdminOverview />} />
      <Route path="/admin/organizations" element={<AdminOrganizations />} />
      <Route path="/admin/users" element={<AdminUsersPage />} />
      <Route path="/admin/payments/proposals" element={<AdminProposals />} />
      <Route path="/admin/payments/invoices" element={<AdminInvoices />} />
      <Route path="/admin/api/subscriptions" element={<AdminSubscriptions />} />
      <Route path="/admin/api/plans" element={<AdminPlans />} />
      <Route path="/admin/api/model-pricing" element={<AdminModelPricingPage />} />
      <Route path="/admin/observability" element={<AdminObservabilityPage />} />
      <Route path="/admin/audit" element={<AdminAudit />} />
    </>
  );
}

function OwnerRoutes() {
  return (
    <>
      <Route path="/" element={<OwnerOverview />} />
      <Route path="/brain" element={<OwnerBrainPage />} />
      <Route path="/billing" element={<OwnerBilling />} />
      <Route path="/api/team" element={<OwnerTeam />} />
      <Route path="/api/usage" element={<OwnerUsage />} />
      {/* Legacy redirects so old emails / bookmarks still work. */}
      <Route path="/api/subscription" element={<OwnerBilling />} />
      <Route path="/api/workspaces" element={<Navigate to="/" replace />} />
      <Route path="/settings/fiscal" element={<OwnerBilling />} />
      <Route path="/payments/proposals" element={<OwnerBilling />} />
    </>
  );
}

function MemberRoutes() {
  return (
    <>
      <Route path="/" element={<MemberOverview />} />
      {/* Org admins (account_type enterprise_member, org role "admin") curate
          the brain too. The route is open to every member; the API gate
          (requireOrgAdmin) denies plain members, and the sidebar only shows
          the entry to admins. */}
      <Route path="/brain" element={<OwnerBrainPage />} />
      <Route path="/api/usage" element={<OwnerUsage />} />
    </>
  );
}

function AccessDenied({
  email,
  onSignOut,
}: {
  email: string;
  onSignOut: () => void;
}) {
  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-6">
      <div className="max-w-sm bg-surface border border-border rounded-2xl p-8 text-center shadow-card">
        <Shield className="w-10 h-10 text-ink-3 mx-auto mb-4" />
        <h1 className="text-2xl font-black text-ink mb-3">Access denied</h1>
        <p className="text-sm text-ink-2 mb-6">
          Signed in as <strong>{email}</strong>, but this account does not have
          access to this app.
        </p>
        <button
          onClick={onSignOut}
          className="bg-ink text-white text-sm font-medium px-5 py-2 rounded-full hover:opacity-80 transition-opacity"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
